import { Component, Input, OnInit } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';

export type ChartType = 'bar' | 'line' | 'scatter' | 'bubble' | 'pie' | 'doughnut' | 'polarArea' | 'radar';

@Component({
  selector: 'app-dashboard-chart',
  standalone: true,
  imports: [ChartModule, CommonModule, TooltipModule],
  template: `
    <div class="dashboard-chart"
         [pTooltip]="getChartTooltipText()"
         tooltipPosition="top"
         appendTo="body"
         [showDelay]="500"
         [hideDelay]="0"
         [tooltipStyleClass]="'custom-tooltip'">
      <h4>{{ title }}</h4>
      <p-chart [type]="type" [data]="data" [options]="options"></p-chart>
    </div>
  `,
  styleUrls: ['./dashboard-chart.component.scss']
})
export class DashboardChartComponent implements OnInit {
  @Input() title: string = '';
  @Input() type: ChartType = 'bar';
  @Input() data: any;
  @Input() options: any;

  ngOnInit() {
    if (!this.options) {
      this.options = this.getDefaultOptions();
    }
  }

  private getDefaultOptions(): any {
    if (this.type === 'pie' || this.type === 'doughnut') {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true,
              font: {
                size: 12
              },
              generateLabels: (chart: any) => {
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                  return data.labels.map((label: string, i: number) => {
                    const dataset = data.datasets[0];
                    const value = dataset.data[i];
                    const total = dataset.data.reduce((a: number, b: number) => a + b, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                    
                    return {
                      text: `${label} (${percentage}%)`,
                      fillStyle: dataset.backgroundColor[i],
                      strokeStyle: dataset.backgroundColor[i],
                      pointStyle: 'circle',
                      hidden: false,
                      index: i
                    };
                  });
                }
                return [];
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const value = context.parsed;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${percentage}%)`;
              }
            }
          }
        }
      };
    }
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top'
        }
      }
    };
  }

  getChartTooltipText(): string {
    switch (this.title) {
      case 'Evolução Financeira Mensal':
      case 'Evolução Financeira Mensal (Baseado em Extratos Importados)':
        return 'Gráfico de barras mostrando a evolução das receitas e despesas baseada nos extratos bancários importados ao longo do ano, permitindo visualizar tendências financeiras mensais';
      
      case 'Receitas por Cliente':
        return 'Gráfico de pizza mostrando a distribuição das receitas por cliente, com percentuais e valores totais';
      
      case 'NFS-e: Emitidas x Importadas':
        return 'Comparativo entre NFS-e emitidas pelo sistema e NFS-e importadas de outros sistemas, mostrando o volume de cada tipo';
      
      default:
        return this.title;
    }
  }
} 
